// src/services/mappers/businessMapper.ts
import type { CorporateEntityDto, CorporateClientSummaryDto } from '@generated/api';
import type { CorporateEntitySummary } from '@typeDefs/domain/entities';
import { CorporateClientType as ApiCorporateEntityType } from '@generated/api';
import { CorporateEntityType, DEFAULT_CHARGE_RATE_PER_ANNUM } from '@typeDefs/domain/entities';
import { CorporateEntityProfile } from '@typeDefs/domain/profile';
import { accountMapper } from './accountMapper';

export function mapCorporateEntityTypeApiToDomain(
  apiType?: ApiCorporateEntityType,
): CorporateEntityType | undefined {
  switch (apiType) {
    case ApiCorporateEntityType.Value0:
      return CorporateEntityType.Business;
    case ApiCorporateEntityType.Value1:
      return CorporateEntityType.Charity;
    default:
      return CorporateEntityType.Business;
  }
}

export const corporateEntityMapper = {
  fromApi: (apiData: CorporateEntityDto): CorporateEntityProfile => ({
    id: apiData.id?.toString() || '',
    name: apiData.name || '',
    registrationNumber: apiData.registrationNumber || '',
    businessType: undefined, // Not in API response
    virtualCashAccount: apiData.virtualCashAccount
      ? accountMapper.fromApi(apiData.virtualCashAccount)
      : undefined,
  }),

  toApi: (domainData: Partial<CorporateEntityProfile>): Partial<CorporateEntityDto> => ({
    name: domainData.name,
    registrationNumber: domainData.registrationNumber,
    virtualCashAccount: domainData.virtualCashAccount
      ? accountMapper.toApi(domainData.virtualCashAccount)
      : undefined,
  }),

  summaryFromApi: (apiData: CorporateClientSummaryDto): CorporateEntitySummary => {
    // bug-75: single choke point for the management-fee rate. Units are PERCENT
    // (0.3 = 0.3%/yr). `??` not `||` — 0 is a legitimate zero-fee rate. Missing or
    // non-finite input falls back to the platform default; the FEE is clamped ≥ 0
    // (net rate is deliberately NOT clamped — negative-yield bills are real).
    const rate = Number.isFinite(apiData.chargeRatePerAnnum as number)
      ? (apiData.chargeRatePerAnnum as number)
      : undefined;

    return {
      id: apiData.id || '',
      name: apiData.name || '',
      registrationNumber: apiData.registrationNumber || '',
      type: mapCorporateEntityTypeApiToDomain(apiData.type), // Default to Business if type is not provided
      chargeRatePerAnnum: Math.max(rate ?? DEFAULT_CHARGE_RATE_PER_ANNUM, 0),
    };
  },
};
